import { useState, useEffect, useCallback } from 'react';
import { Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, RefreshCw, Clock, Radio } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useVoice } from '@/context/VoiceContext';
import { toast } from 'sonner';
import RecordingPlayer from '@/components/voice/RecordingPlayer';

const TABS = [
  { id: 'all', label: 'All' },
  { id: 'inbound', label: 'Incoming' },
  { id: 'outbound', label: 'Outgoing' },
  { id: 'missed', label: 'Missed' },
];

function formatDuration(secs) {
  if (!secs) return null;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

function timeAgo(isoStr) {
  if (!isoStr) return '—';
  const diff = Date.now() - new Date(isoStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(isoStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function DirectionIcon({ direction, status }) {
  const isMissed = ['no-answer', 'missed', 'canceled'].includes(status) && !direction?.includes('outbound');
  const isOutbound = direction?.includes('outbound');

  if (isMissed) return (
    <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
      <PhoneMissed className="w-3.5 h-3.5 text-red-500" />
    </div>
  );
  if (isOutbound) return (
    <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
      <PhoneOutgoing className="w-3.5 h-3.5 text-blue-500" />
    </div>
  );
  return (
    <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
      <PhoneIncoming className="w-3.5 h-3.5 text-emerald-500" />
    </div>
  );
}

function normalizePhone(raw) {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return raw.startsWith('+') ? raw : `+${digits}`;
}

function CallRow({ call, onCallback }) {
  const navigate = useNavigate();
  const isOutbound = call.direction?.includes('outbound');
  const isMissed = ['no-answer', 'missed', 'canceled'].includes(call.status) && !isOutbound;
  const otherNumber = isOutbound ? call.to_number : call.from_number;
  const dur = formatDuration(call.duration);
  const hasRecording = !!call.recording_sid;
  const recDur = call.recording_duration || call.duration || 0;

  return (
    <div
      className={`
        px-4 py-2.5 group
        hover:bg-slate-50 cursor-pointer transition-colors border-b border-slate-50
        ${isMissed ? 'hover:bg-red-50/40' : ''}
      `}
      onClick={() => call.contact_id ? navigate(`/contacts/${call.contact_id}`) : null}
    >
      <div className="flex items-center gap-2.5">
      <DirectionIcon direction={call.direction} status={call.status} />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1">
          {call.contact_name ? (
            <p className={`text-sm font-medium truncate ${isMissed ? 'text-red-600' : 'text-slate-800'}`}>
              {call.contact_name}
            </p>
          ) : (
            <p className="text-sm font-mono truncate text-slate-700">{otherNumber || '—'}</p>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          {call.contact_name && (
            <span className="text-[10px] font-mono text-slate-400 truncate">{otherNumber}</span>
          )}
          <span className="text-[10px] text-slate-400">{timeAgo(call.created_at)}</span>
          {dur && (
            <span className="flex items-center gap-0.5 text-[10px] text-slate-400">
              <Clock className="w-2.5 h-2.5" />{dur}
            </span>
          )}
        </div>
      </div>

      <button
        data-testid={`recent-callback-${call.twilio_call_sid}`}
        onClick={(e) => { e.stopPropagation(); if (otherNumber) onCallback(otherNumber); }}
        className="
          opacity-0 group-hover:opacity-100
          w-7 h-7 rounded-full bg-emerald-50 text-emerald-600 hover:bg-emerald-100
          flex items-center justify-center flex-shrink-0 transition-all
        "
        title="Call back"
      >
        <Phone className="w-3 h-3" strokeWidth={2.5} />
      </button>
      </div>

      {hasRecording && (
        <div className="mt-2 pl-10 pr-1" data-testid={`recent-recording-${call.twilio_call_sid}`}>
          <RecordingPlayer callSid={call.twilio_call_sid} duration={recDur} />
        </div>
      )}
    </div>
  );
}

export default function RecentsPanel({ refreshTrigger = 0 }) {
  const navigate = useNavigate();
  const { makeCall, device, callStatus } = useVoice();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [recordingOn, setRecordingOn] = useState(false);

  useEffect(() => {
    api.get('/voice/recording-settings')
      .then(r => setRecordingOn(!!r.data?.enabled))
      .catch(() => {});
  }, []);

  const fetchCalls = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/voice/calls?limit=100');
      setCalls(res.data.calls || []);
    } catch {
      setCalls([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCalls();
  }, [fetchCalls, refreshTrigger]);

  const filtered = calls.filter(c => {
    if (activeTab === 'all') return true;
    if (activeTab === 'inbound') return !c.direction?.includes('outbound');
    if (activeTab === 'outbound') return c.direction?.includes('outbound');
    if (activeTab === 'missed') {
      return ['no-answer', 'missed', 'canceled'].includes(c.status) && !c.direction?.includes('outbound');
    }
    return true;
  });

  const missedCount = calls.filter(c =>
    ['no-answer', 'missed', 'canceled'].includes(c.status) && !c.direction?.includes('outbound')
  ).length;

  const handleCallback = async (number) => {
    if (!device) { toast.error('Voice service not available'); return; }
    if (callStatus !== 'idle') { toast.warning('Already on a call'); return; }
    try {
      await makeCall(normalizePhone(number), number);
    } catch (err) {
      toast.error(`Call failed: ${err.message}`);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Tabs */}
      <div className="flex items-center gap-0.5 px-3 pt-3 pb-2 border-b border-slate-100">
        <div className="flex gap-0.5 flex-1 bg-slate-100 rounded-lg p-0.5">
          {TABS.map(t => (
            <button
              key={t.id}
              data-testid={`recents-tab-${t.id}`}
              onClick={() => setActiveTab(t.id)}
              className={`
                flex-1 py-1.5 text-[11px] font-semibold rounded-md transition-all relative
                ${activeTab === t.id
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
                }
              `}
            >
              {t.label}
              {t.id === 'missed' && missedCount > 0 && (
                <span className="absolute -top-1 -right-0.5 w-3.5 h-3.5 bg-red-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center">
                  {missedCount > 9 ? '9+' : missedCount}
                </span>
              )}
            </button>
          ))}
        </div>
        <button
          onClick={fetchCalls}
          className="ml-1.5 p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-all"
          title="Refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {loading && calls.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-slate-400">
            <div className="w-5 h-5 border-2 border-slate-200 border-t-slate-500 rounded-full animate-spin mr-2" />
            <span className="text-sm">Loading...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-400">
            <Phone className="w-8 h-8 mb-2 opacity-30" />
            <p className="text-sm">No {activeTab !== 'all' ? activeTab : ''} calls</p>
          </div>
        ) : (
          filtered.map(c => (
            <CallRow key={c.twilio_call_sid || c.created_at} call={c} onCallback={handleCallback} />
          ))
        )}
      </div>

      <div className="px-4 py-2 border-t border-slate-100 space-y-1">
        <p className="text-[10px] text-slate-400">{filtered.length} call{filtered.length !== 1 ? 's' : ''}</p>
        {recordingOn && (
          <p className="text-[10px] text-slate-400 flex items-start gap-1" data-testid="recents-recording-note">
            <Radio className="w-2.5 h-2.5 mt-0.5 text-red-500 flex-shrink-0" />
            Recording is on. Recorded calls show a player — history starts from now.
          </p>
        )}
      </div>
    </div>
  );
}
